import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Youtube } from 'lucide-react';
import { useSiteSettings } from '../contexts/SiteSettingsContext';

const Footer = () => {
  const { settings } = useSiteSettings();
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-gradient-to-br from-gray-900 to-gray-800 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <img 
                src={settings.logo} 
                alt="AKM Laser" 
                className="h-12 w-12"
              />
              <span className="text-xl font-bold">AKM Laser</span>
            </div>
            <p className="text-gray-400 text-sm leading-relaxed">
              Experts en machines laser depuis 2015. Vente, installation, formation et SAV de machines laser CO2, fibre et mixtes pour professionnels.
            </p>
            <div className="flex space-x-3 pt-4">
              <a href="https://www.youtube.com/@AKMLaser" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-lg bg-gray-800 hover:bg-#F5EBDD flex items-center justify-center transition-all duration-300 group" aria-label="YouTube AKM Laser" data-testid="social-youtube">
                <Youtube className="w-5 h-5 text-gray-400 group-hover:text-gray-900" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold mb-6 text-#F5EBDD">Navigation</h3>
            <ul className="space-y-3">
              {[
                { path: '/', label: 'Accueil' },
                { path: '/machines', label: 'Nos Machines' },
                { path: '/services', label: 'Services' },
                { path: '/realisations', label: 'Réalisations' },
                { path: '/a-propos', label: 'À propos' },
                { path: '/contact', label: 'Contact' }
              ].map((link) => (
                <li key={link.path}>
                  <Link to={link.path} className="text-gray-400 hover:text-#F5EBDD transition-colors duration-300 text-sm">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Machine Categories */}
          <div>
            <h3 className="text-lg font-semibold mb-6 text-#F5EBDD">Nos Machines</h3>
            <ul className="space-y-3">
              <li>
                <Link to="/machines?type=co2" className="text-gray-400 hover:text-#F5EBDD transition-colors duration-300 text-sm">
                  Laser CO2
                </Link>
              </li>
              <li>
                <Link to="/machines?type=fiber" className="text-gray-400 hover:text-#F5EBDD transition-colors duration-300 text-sm">
                  Laser Fibre
                </Link>
              </li>
              <li>
                <Link to="/machines?type=mixed" className="text-gray-400 hover:text-#F5EBDD transition-colors duration-300 text-sm">
                  Machines Mixtes
                </Link>
              </li>
              <li>
                <Link to="/occasions" className="text-gray-400 hover:text-#F5EBDD transition-colors duration-300 text-sm">
                  Occasions
                </Link>
              </li>
              <li>
                <Link to="/location" className="text-gray-400 hover:text-#F5EBDD transition-colors duration-300 text-sm">
                  Location
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-semibold mb-6 text-#F5EBDD">Contact</h3>
            <ul className="space-y-4">
              <li className="flex items-start space-x-3">
                <Mail className="w-5 h-5 text-#F5EBDD mt-0.5 flex-shrink-0" />
                <a href={`mailto:${settings.contactEmail}`} className="text-gray-400 hover:text-#F5EBDD transition-colors duration-300 text-sm">
                  {settings.contactEmail}
                </a>
              </li>
              <li className="flex items-start space-x-3">
                <Phone className="w-5 h-5 text-#F5EBDD mt-0.5 flex-shrink-0" />
                <a href={`tel:${settings.contactPhone.replace(/\s/g, '')}`} className="text-gray-400 hover:text-#F5EBDD transition-colors duration-300 text-sm">
                  {settings.contactPhone}
                </a>
              </li>
              <li className="flex items-start space-x-3">
                <MapPin className="w-5 h-5 text-#F5EBDD mt-0.5 flex-shrink-0" />
                <span className="text-gray-400 text-sm">
                  {settings.contactAddress}<br />
                  Sur rendez-vous uniquement
                </span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-700 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
          <p className="text-gray-400 text-sm">
            © {currentYear} AKM Laser. Tous droits réservés.
          </p>
          <div className="flex space-x-6 text-sm">
            <Link to="/mentions-legales" className="text-gray-400 hover:text-#F5EBDD transition-colors duration-300">
              Mentions légales
            </Link>
            <Link to="/confidentialite" className="text-gray-400 hover:text-#F5EBDD transition-colors duration-300">
              Confidentialité
            </Link>
            <Link to="/cgv" className="text-gray-400 hover:text-#F5EBDD transition-colors duration-300">
              CGV
            </Link>
            <Link to="/occasions" className="text-gray-400 hover:text-#F5EBDD transition-colors duration-300">
              Occasions
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
